import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, X, Phone, Play, Youtube, ShoppingCart, Zap, Truck } from "lucide-react";
import { publicApi, resolveImageUrl } from "../lib/api";
import { useCart } from "../lib/CartContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";

// Helper function to convert YouTube URLs to embed format
const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    // Already an embed URL
    if (url.includes('/embed/')) return url;
    
    // Extract video ID from various YouTube URL formats
    let videoId = null;
    
    // youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) videoId = watchMatch[1];
    
    // youtu.be/VIDEO_ID
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) videoId = shortMatch[1];
    
    // youtube.com/v/VIDEO_ID
    const vMatch = url.match(/youtube\.com\/v\/([^?&]+)/);
    if (vMatch) videoId = vMatch[1];
    
    if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
    }
    
    // Return original if can't parse (might be Vimeo or other)
    return url;
};

const ProductDetailPage = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    
    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            try {
                const res = await publicApi.getProduct(productId);
                setProduct(res.data);
                // Fetch related products if any
                if (res.data.related_product_ids?.length > 0) {
                    const allRes = await publicApi.getProducts();
                    const related = allRes.data.filter(p => res.data.related_product_ids.includes(p.id));
                    setRelatedProducts(related);
                } else {
                    setRelatedProducts([]);
                }
            } catch (err) {
                console.error("Failed to fetch product:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [productId]);
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!product) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center">
                <p className="text-muted-foreground mb-4">Product not found</p>
                <Link to="/products">
                    <Button variant="outline" className="rounded-none">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Products
                    </Button>
                </Link>
            </div>
        );
    }
    
    return (
        <div className="noise-overlay min-h-screen">
            {/* Breadcrumb */}
            <div className="border-b border-border">
                <div className="container mx-auto px-6 lg:px-12 py-4">
                    <nav className="flex items-center gap-2 text-sm">
                        <Link to="/products" className="text-muted-foreground hover:text-primary transition-colors">
                            Equipment
                        </Link>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-foreground">{product.display_title}</span>
                    </nav>
                </div>
            </div>
            
            {/* Main Content */}
            <div className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Image Gallery */}
                    <div>
                        <div className="aspect-[4/3] bg-card border border-border overflow-hidden mb-4 relative group">
                            {product.images?.[selectedImage] ? (
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <img 
                                            src={resolveImageUrl(product.images[selectedImage])} 
                                            alt={product.display_title}
                                            className="w-full h-full object-contain cursor-zoom-in"
                                        />
                                    </DialogTrigger>
                                    <DialogContent className="max-w-4xl bg-card border-border">
                                        <img 
                                            src={resolveImageUrl(product.images[selectedImage])} 
                                            alt={product.display_title}
                                            className="w-full h-auto"
                                        />
                                    </DialogContent>
                                </Dialog>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <span className="text-muted-foreground font-mono">No Image Available</span>
                                </div>
                            )}
                            
                            {/* Previous / Next Arrows */}
                            {product.images?.length > 1 && (
                                <>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev <= 0 ? product.images.length - 1 : prev - 1); }}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        data-testid="image-prev-btn"
                                    >
                                        <ArrowLeft className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev >= product.images.length - 1 ? 0 : prev + 1); }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        data-testid="image-next-btn"
                                    >
                                        <ArrowRight className="h-5 w-5" />
                                    </button>
                                    <span className="absolute bottom-3 left-3 bg-black/50 text-white text-xs font-mono px-2 py-1">
                                        {selectedImage + 1} / {product.images.length}
                                    </span>
                                </>
                            )}}
                            
                            {product.video_embed && (
                                <div className="absolute bottom-4 right-4">
                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <Button className="bg-primary text-primary-foreground rounded-none gap-2">
                                                <Play className="h-4 w-4" />
                                                Watch Video
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-4xl bg-card border-border">
                                            <div className="aspect-video">
                                                <iframe 
                                                    src={getYouTubeEmbedUrl(product.video_embed)}
                                                    className="w-full h-full"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    title="Product Video"
                                                />
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                            )}
                        </div>
                        
                        {/* Thumbnails */}
                        {product.images?.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto pb-2">
                                {product.images.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedImage(index)}
                                        className={`w-20 h-20 flex-shrink-0 border-2 transition-colors ${
                                            selectedImage === index ? "border-primary" : "border-border hover:border-primary/50"
                                        }`}
                                    >
                                        <img src={resolveImageUrl(img)} alt={`View ${index + 1}`} className="w-full h-full object-contain" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Product Info */}
                    <div>
                        <p className="font-mono text-xs text-muted-foreground mb-2">{product.internal_name}</p>
                        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight mb-4" data-testid="product-title">
                            {product.display_title}
                        </h1>
                        
                        {product.manufacturer && (
                            <p className="text-muted-foreground/60 text-sm mb-4">
                                Manufactured by <span className="text-foreground">{product.manufacturer}</span> · Official Sales & Service by <span className="text-foreground">{product.sales_service_provider || "Dry Ice Bulls"}</span>
                            </p>
                        )}
                        
                        <p className="text-muted-foreground mb-6">{product.short_description}</p>
                        
                        {/* Stock Status */}
                        {(() => {
                            const status = product.availability_status || "in_stock";
                            const statusMap = {
                                in_stock: { color: "bg-green-500", text: "In Stock - Ready to Ship" },
                                available_to_order: { color: "bg-yellow-500", text: "Available to Order - Ships within 10 Days" },
                                factory_order: { color: "bg-blue-500", text: "Factory Order - Custom Freight Config Required (Est. Delivery 14-21 Days)" }
                            };
                            const s = statusMap[status] || statusMap.in_stock;
                            return (
                                <div className="flex items-center gap-2 mb-4">
                                    <div className={`w-3 h-3 rounded-full ${s.color}`} />
                                    <span className="text-sm font-bold text-foreground">{s.text}</span>
                                </div>
                            );
                        })()}

                        <div className="bg-card border border-border p-6 mb-4">
                            <p className="font-mono text-2xl text-primary font-bold mb-1" data-testid="product-price">
                                {product.price_text}
                            </p>
                            <div className="flex items-center gap-2 text-muted-foreground text-xs mt-1">
                                <Truck className="h-3 w-3" />
                                {product.shipping_label || ((product.shipping_type || "heavy_machinery") === "heavy_machinery"
                                    ? "Insured Nationwide Flat-Rate Freight Shipping"
                                    : "Standard Ground Shipping")} (${(product.shipping_cost ?? ((product.shipping_type || "heavy_machinery") === "heavy_machinery" ? 350 : 15)).toLocaleString('en-US', { minimumFractionDigits: 2 })}) — 48 Lower States
                            </div>
                            {(() => {
                                const price = parseFloat((product.price_text || "").replace(/[^0-9.]/g, "")) || 0;
                                const monthly = product.monthly_payment_text || (price > 0 ? `$${(price / 48).toFixed(2)}/month` : "");
                                if (monthly) return (
                                    <p className="text-xs mt-2 text-muted-foreground">
                                        Financing available: as low as <span className="text-primary font-bold">{monthly}</span>*
                                    </p>
                                );
                                return null;
                            })()}
                        </div>
                        
                        {/* CTAs based on whether product has a price */}
                        {(() => {
                            const price = parseFloat((product.price_text || "").replace(/[^0-9.]/g, "")) || 0;
                            if (price > 0) {
                                return (
                                    <>
                                        <div className="flex flex-col sm:flex-row gap-3 mb-3">
                                            <Button 
                                                onClick={() => { addItem(product); toast.success("Added to cart!"); }}
                                                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider py-6"
                                                data-testid="add-to-cart-btn"
                                            >
                                                <ShoppingCart className="mr-2 h-5 w-5" /> Add to Cart
                                            </Button>
                                            <Button 
                                                onClick={() => { addItem(product); navigate("/checkout"); }}
                                                variant="outline"
                                                className="flex-1 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-none uppercase font-bold tracking-wider py-6"
                                                data-testid="buy-now-btn"
                                            >
                                                <Zap className="mr-2 h-5 w-5" /> Buy Now
                                            </Button>
                                        </div>
                                        <Link to="/guidance" className="block text-center text-muted-foreground text-xs hover:text-primary transition-colors mb-6">
                                            Not sure if this is the right machine for your application? Click here to get expert guidance from our 20-year service desk before buying.
                                        </Link>
                                    </>
                                );
                            }
                            return (
                                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                                    <Link to={`/quote/${product.id}`} className="flex-1">
                                        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider py-6" data-testid="request-quote-btn">
                                            Request Custom Quote <ArrowRight className="ml-2 h-5 w-5" />
                                        </Button>
                                    </Link>
                                    <Link to="/guidance" className="flex-1">
                                        <Button variant="outline" className="w-full border-2 rounded-none uppercase font-bold tracking-wider py-6" data-testid="get-guidance-btn">
                                            <Phone className="mr-2 h-5 w-5" /> Get Guidance
                                        </Button>
                                    </Link>
                                </div>
                            );
                        })()}
                        
                        {/* JSON-LD Schema */}
                        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "Product",
                            "name": `${product.manufacturer || ""} ${product.display_title}`.trim(),
                            "image": product.images || [],
                            "description": product.short_description,
                            "sku": product.internal_name,
                            "mpn": product.internal_name,
                            "brand": { "@type": "Brand", "name": product.manufacturer || "Dry Ice Marketplace" },
                            "offers": {
                                "@type": "Offer",
                                "url": `https://dryicemarketplace.com/products/${product.id}`,
                                "priceCurrency": "USD",
                                "price": (parseFloat((product.price_text || "").replace(/[^0-9.]/g, "")) || 0).toFixed(2),
                                "itemCondition": (product.internal_name || "").toUpperCase().includes("USED") ? "https://schema.org/UsedCondition" : "https://schema.org/NewCondition",
                                "availability": (() => {
                                    const s = product.availability_status || "in_stock";
                                    if (s === "factory_order") return "https://schema.org/PreOrder";
                                    if (s === "available_to_order") return "https://schema.org/BackOrder";
                                    return "https://schema.org/InStock";
                                })(),
                                "seller": { "@type": "Organization", "name": product.sales_service_provider || "Dry Ice Bulls" },
                                "shippingDetails": {
                                    "@type": "OfferShippingDetails",
                                    "shippingRate": { "@type": "MonetaryAmount", "value": (product.shipping_type || "heavy_machinery") === "heavy_machinery" ? "350.00" : "15.00", "currency": "USD" },
                                    "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "US" }
                                }
                            }
                        })}} />
                        
                        {/* Quick Specs */}
                        {product.technical_specs?.length > 0 && (
                            <div className="border-t border-border pt-6">
                                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Key Specifications</p>
                                <div className="grid grid-cols-2 gap-4">
                                    {product.technical_specs.slice(0, 6).map((spec, index) => (
                                        <div key={index} className="bg-secondary/50 p-3">
                                            <p className="text-muted-foreground text-xs mb-1">{spec.name}</p>
                                            <p className="font-mono font-bold">
                                                {spec.value} {spec.unit && <span className="text-muted-foreground font-normal">{spec.unit}</span>}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Detailed Information Tabs */}
                <div className="mt-12 lg:mt-16">
                    <Tabs defaultValue="overview" className="w-full">
                        <TabsList className="bg-card border border-border rounded-none w-full justify-start overflow-x-auto">
                            <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                                Overview
                            </TabsTrigger>
                            <TabsTrigger value="specs" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                                Specifications
                            </TabsTrigger>
                            <TabsTrigger value="applications" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                                Applications
                            </TabsTrigger>
                            {product.video_embed && (
                                <TabsTrigger value="video" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                                    <Youtube className="h-4 w-4 mr-2" />
                                    Video
                                </TabsTrigger>
                            )}
                            {product.upgrade_options?.length > 0 && (
                                <TabsTrigger value="upgrades" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                                    Upgrades
                                </TabsTrigger>
                            )}
                        </TabsList>
                        
                        <TabsContent value="overview" className="mt-6">
                            <Card className="bg-card border border-border rounded-none">
                                <CardContent className="p-6 lg:p-8">
                                    <h3 className="font-heading font-bold text-xl mb-4">Product Overview</h3>
                                    <p className="text-muted-foreground whitespace-pre-line leading-relaxed">
                                        {product.long_description || product.short_description}
                                    </p>
                                </CardContent>
                            </Card>
                        </TabsContent>
                        
                        <TabsContent value="specs" className="mt-6">
                            <Card className="bg-card border border-border rounded-none">
                                <CardContent className="p-6 lg:p-8">
                                    <h3 className="font-heading font-bold text-xl mb-4">Technical Specifications</h3>
                                    {product.technical_specs?.length > 0 ? (
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-border">
                                                    <TableHead className="font-mono uppercase text-xs">Specification</TableHead>
                                                    <TableHead className="font-mono uppercase text-xs">Value</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {product.technical_specs.map((spec, index) => (
                                                    <TableRow key={index} className="border-border">
                                                        <TableCell className="text-muted-foreground">{spec.name}</TableCell>
                                                        <TableCell className="font-mono">
                                                            {spec.value} {spec.unit && <span className="text-muted-foreground">{spec.unit}</span>}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <p className="text-muted-foreground">Specifications not available. Contact us for details.</p>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                        
                        <TabsContent value="applications" className="mt-6">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Primary Use Cases */}
                                <Card className="bg-card border border-border rounded-none">
                                    <CardContent className="p-6">
                                        <h4 className="font-heading font-bold text-lg mb-4">Primary Use Cases</h4>
                                        {product.primary_use_cases?.length > 0 ? (
                                            <ul className="space-y-2">
                                                {product.primary_use_cases.map((use, index) => (
                                                    <li key={index} className="flex items-start gap-2 text-muted-foreground">
                                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                                        {use}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-muted-foreground">Contact us for use case information.</p>
                                        )}
                                    </CardContent>
                                </Card>
                                
                                {/* Best For */}
                                <Card className="bg-card border border-border rounded-none">
                                    <CardContent className="p-6">
                                        <h4 className="font-heading font-bold text-lg mb-4">Best For</h4>
                                        {product.best_for?.length > 0 ? (
                                            <ul className="space-y-2">
                                                {product.best_for.map((item, index) => (
                                                    <li key={index} className="flex items-start gap-2 text-muted-foreground">
                                                        <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-muted-foreground">Contact us for details.</p>
                                        )}
                                    </CardContent>
                                </Card>
                                
                                {/* Not Designed For */}
                                <Card className="bg-card border border-border rounded-none">
                                    <CardContent className="p-6">
                                        <h4 className="font-heading font-bold text-lg mb-4">Not Designed For</h4>
                                        {product.not_designed_for?.length > 0 ? (
                                            <ul className="space-y-2">
                                                {product.not_designed_for.map((item, index) => (
                                                    <li key={index} className="flex items-start gap-2 text-muted-foreground">
                                                        <X className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-muted-foreground">Contact us for details.</p>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>
                        
                        {product.video_embed && (
                            <TabsContent value="video" className="mt-6">
                                <Card className="bg-card border border-border rounded-none">
                                    <CardContent className="p-6 lg:p-8">
                                        <h3 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                                            <Youtube className="h-6 w-6 text-red-500" />
                                            Product Video
                                        </h3>
                                        <div className="aspect-video bg-black">
                                            <iframe 
                                                src={getYouTubeEmbedUrl(product.video_embed)}
                                                className="w-full h-full"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                title="Product Video"
                                            />
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        )}
                        
                        {product.upgrade_options?.length > 0 && (
                            <TabsContent value="upgrades" className="mt-6">
                                <Card className="bg-card border border-border rounded-none">
                                    <CardContent className="p-6 lg:p-8">
                                        <h3 className="font-heading font-bold text-xl mb-4">Available Upgrades</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {product.upgrade_options.map((upgrade, index) => (
                                                <div key={index} className="bg-secondary/50 border border-border p-4">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div>
                                                            <h5 className="font-bold mb-1">{upgrade.name}</h5>
                                                            <p className="text-muted-foreground text-sm">{upgrade.description}</p>
                                                        </div>
                                                        {upgrade.price_text && (
                                                            <Badge className="rounded-none bg-primary/10 text-primary border-0 flex-shrink-0">
                                                                {upgrade.price_text}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        )}
                    </Tabs>
                </div>
                
                {/* Related Products */}
                {relatedProducts.length > 0 && (
                    <div className="mt-12 lg:mt-16">
                        <h2 className="font-heading text-xl font-bold text-foreground mb-6">This Could Also Interest You</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {relatedProducts.map(rp => (
                                <Link
                                    key={rp.id}
                                    to={`/products/${rp.id}`}
                                    className="group bg-card border border-border hover:border-primary/50 transition-all duration-300"
                                    data-testid={`related-product-${rp.id}`}
                                >
                                    <div className="aspect-square bg-muted/30 overflow-hidden flex items-center justify-center p-4">
                                        {rp.images?.[0] ? (
                                            <img src={resolveImageUrl(rp.images[0])} alt={rp.display_title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" />
                                        ) : (
                                            <div className="text-muted-foreground text-sm">No image</div>
                                        )}
                                    </div>
                                    <div className="p-4 border-t border-border">
                                        <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">{rp.manufacturer}</p>
                                        <h3 className="font-heading text-base font-bold text-foreground mb-1 group-hover:text-primary transition-colors">{rp.display_title}</h3>
                                        <p className="text-muted-foreground text-xs line-clamp-2 mb-2">{rp.short_description}</p>
                                        {rp.price_text && <p className="font-mono text-primary font-bold text-sm">{rp.price_text}</p>}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* CTA Section */}
                <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="bg-card border border-border rounded-none tech-corners">
                        <CardContent className="p-6 lg:p-8">
                            <Phone className="h-8 w-8 text-primary mb-4" />
                            <h3 className="font-heading font-bold text-xl mb-2">Get Guidance</h3>
                            <p className="text-muted-foreground mb-4">
                                Not sure if this is the right system for your application? Talk to a Dry Ice Cleaning Specialist.
                            </p>
                            <Link to="/guidance">
                                <Button variant="outline" className="rounded-none uppercase font-bold tracking-wider">
                                    Talk to a Specialist
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-primary rounded-none tech-corners">
                        <CardContent className="p-6 lg:p-8">
                            <ArrowRight className="h-8 w-8 text-primary-foreground mb-4" />
                            <h3 className="font-heading font-bold text-xl mb-2 text-primary-foreground">Ready to Move Forward?</h3>
                            <p className="text-primary-foreground/80 mb-4">
                                Request pricing and availability for the {product.display_title}.
                            </p>
                            <Link to={`/quote/${product.id}`}>
                                <Button className="bg-background text-foreground hover:bg-background/90 rounded-none uppercase font-bold tracking-wider">
                                    Request Quote
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailPage;
